// Window chrome accent presets — the title bars, the taskbar and the Start button.
//
// The fork recoloured 165 Luna blue literals to lime, which made every window chrome lime and,
// in Kris's words, "a little bit too green". This makes that one decision switchable instead of
// baked in, so it can be tried, compared and reverted without touching a single component.
//
// TRY ONE:   add ?chrome=<name> to the URL. It persists, so you can navigate around and judge it.
// REVERT:    ?chrome=lime  — that is exactly what shipped before this file existed.
// CLEAR:     ?chrome=off   — forgets the choice and falls back to the default below.
//
// Changing DEFAULT below is the only edit needed to ship a different one for everyone.
//
// The gradients keep the real multi-stop shape Luna actually used. A two-stop approximation looks
// flat and modern; the extra stops are what make a 2001 title bar read as plastic.

import React from 'react';

// Bumped to :2 when lime became the default again. Anyone who tried ?chrome=balanced or =luna had
// that choice persisted, and without a new key they would keep seeing blue after the revert and
// reasonably conclude it never shipped. A new key discards those stored trials exactly once.
const STORE_KEY = 'ic:chrome2';

/**
 * What ships for a visitor who has never chosen.
 *
 * Kris tried the blue variants on 2026-09-13 and ruled against them: "scrap everything I said
 * about adding the blue in the GUI". Lime is the answer and this is the revert. The other four
 * presets stay in the file because they cost nothing and make the next reroll a one-word change,
 * but nothing ships blue unless this line says so.
 */
export const DEFAULT = 'lime';

/**
 * Each preset supplies: the active and inactive title bar, the taskbar body, the Start button,
 * and the small accents (resize grips, focus glow). Hue is the only thing that varies — the
 * stop positions are Luna's own.
 */
export const PRESETS = {
  // Exactly what shipped before. The revert target; do not edit this one.
  lime: {
    label: 'Lime (current)',
    note: 'What the site has now. All chrome lime.',
    titleActive:
      'linear-gradient(to bottom,#beee00 0%,#d7ff35 4%,#d4ff28 6%,#d0ff12 8%,#ccff00 10%,#c0f000 14%,#b7e500 20%,#b6e300 24%,#bceb00 56%,#c4f500 66%,#ccff01 76%,#bfef00 86%,#abd600 92%,#89ab00 94%,#759200 100%)',
    titleInactive:
      'linear-gradient(to bottom,#d2ea73 0%,#d0e67b 3%,#d8ea92 6%,#daeb95 8%,#d2e680 14%,#cfe579 17%,#cbe176 25%,#cee478 56%,#d6ec7f 81%,#d4ea7d 89%,#cee478 94%,#cce277 97%,#d9e4aa 100%)',
    grip: '#c0eb11',
    btnGlow: '#daff46',
    btnFace:
      'radial-gradient(circle at 90% 90%,#bae900 0%,#b4d91e 55%,#c6e840 70%,#deeea1 90%,white 100%)',
    titleText: '#1d2a00',
    taskbar: 'linear-gradient(to bottom,#b6e300 0%,#ccff00 3%,#c4f500 6%,#bceb00 10%,#b7e500 88%,#9fc800 96%,#89ab00 100%)',
    start: 'linear-gradient(to bottom,#d7ff35,#ccff00 40%,#a8d400 90%,#89ab00)',
    startText: '#1d2a00',
    accent: '#ccff00',
  },

  // Original Windows XP. No green anywhere in the chrome.
  luna: {
    label: 'Luna Blue (original XP)',
    note: 'The real thing. Zero green in the chrome; the coin stays lime everywhere else.',
    titleActive:
      'linear-gradient(to bottom,#0058ee 0%,#3a93ff 4%,#288eff 6%,#1a86ff 8%,#0f7eff 10%,#0d78f2 14%,#0b6fe0 20%,#0a6cd9 24%,#0c72e3 56%,#0f7bf0 66%,#1385ff 76%,#0d76ec 86%,#0a63c8 92%,#0a52a5 94%,#08458c 100%)',
    titleInactive:
      'linear-gradient(to bottom,#7ba2e0 0%,#86abe4 3%,#98b9ea 6%,#9cbceb 8%,#8bafe6 14%,#85aae4 17%,#7fa5e1 25%,#82a8e3 56%,#8fb2e8 81%,#8bafe6 89%,#82a8e3 94%,#7fa5e1 97%,#aabfdd 100%)',
    grip: '#3a93ff',
    btnGlow: '#8cc3ff',
    btnFace:
      'radial-gradient(circle at 90% 90%,#1f6fd0 0%,#2f86e8 55%,#5aa8f5 70%,#b9d9f8 90%,white 100%)',
    titleText: '#ffffff',
    taskbar: 'linear-gradient(to bottom,#245edb 0%,#3f8cf3 3%,#2f7de0 6%,#2a72d4 10%,#1f5fc0 88%,#1a52a8 96%,#164a96 100%)',
    start: 'linear-gradient(to bottom,#61b761,#3c9f3c 40%,#2f7d2f 90%,#286b28)',
    startText: '#ffffff',
    accent: '#3a93ff',
  },

  // Luna blue windows, lime where the brand actually earns it. My recommendation.
  balanced: {
    label: 'Balanced (Luna blue + lime accents)',
    note: 'Blue title bars so text reads, lime Start button and tray so the coin still shows.',
    titleActive:
      'linear-gradient(to bottom,#0058ee 0%,#3a93ff 4%,#288eff 6%,#1a86ff 8%,#0f7eff 10%,#0d78f2 14%,#0b6fe0 20%,#0a6cd9 24%,#0c72e3 56%,#0f7bf0 66%,#1385ff 76%,#0d76ec 86%,#0a63c8 92%,#0a52a5 94%,#08458c 100%)',
    titleInactive:
      'linear-gradient(to bottom,#8fa9c9 0%,#9ab3d2 3%,#a9c0da 6%,#adc3dc 8%,#9db6d5 14%,#97b1d2 17%,#92accf 25%,#94aed1 56%,#9fb8d7 81%,#9bb5d5 94%,#b4c2d4 100%)',
    grip: '#ccff00',
    btnGlow: '#8cc3ff',
    btnFace:
      'radial-gradient(circle at 90% 90%,#1f6fd0 0%,#2f86e8 55%,#5aa8f5 70%,#b9d9f8 90%,white 100%)',
    titleText: '#ffffff',
    taskbar: 'linear-gradient(to bottom,#245edb 0%,#3f8cf3 3%,#2f7de0 6%,#2a72d4 10%,#1f5fc0 88%,#1a52a8 96%,#164a96 100%)',
    start: 'linear-gradient(to bottom,#d7ff35,#ccff00 40%,#a8d400 90%,#89ab00)',
    startText: '#1d2a00',
    accent: '#ccff00',
  },

  // XP actually shipped an Olive Green Luna theme. This is the historically correct green XP,
  // and it is a much softer green than the coin's lime.
  olive: {
    label: 'Olive Green (real XP theme)',
    note: 'Windows XP shipped this. Green chrome, but 2001 green rather than neon.',
    titleActive:
      'linear-gradient(to bottom,#7a8b52 0%,#a3b47a 4%,#9aac6f 6%,#93a667 8%,#8ca05f 10%,#879a5b 14%,#7f9254 20%,#7d9052 24%,#819556 56%,#879d5c 66%,#8fa663 76%,#829855 86%,#6f8447 92%,#5d6f3b 94%,#4e5d31 100%)',
    titleInactive:
      'linear-gradient(to bottom,#b9c19c 0%,#c2c9a7 3%,#ccd2b4 6%,#ced4b7 8%,#c4cbab 14%,#bfc7a5 17%,#bac2a0 25%,#bcc4a2 56%,#c6cdad 81%,#c2c9a8 94%,#cdd0bd 100%)',
    grip: '#a3b47a',
    btnGlow: '#c3d68a',
    btnFace:
      'radial-gradient(circle at 90% 90%,#6f8447 0%,#89a05a 55%,#a3b87a 70%,#d2dbb8 90%,white 100%)',
    titleText: '#ffffff',
    taskbar: 'linear-gradient(to bottom,#6f7f4a 0%,#8b9c60 3%,#83945a 6%,#7c8d54 10%,#6b7b46 88%,#5c6a3c 96%,#4e5a33 100%)',
    start: 'linear-gradient(to bottom,#a8c24f,#8fae34 40%,#6f8c22 90%,#5d7a1c)',
    startText: '#ffffff',
    accent: '#ccff00',
  },

  // The third theme XP shipped. Neutral, lets the lime accents carry all the colour.
  silver: {
    label: 'Silver (real XP theme)',
    note: 'The quietest option. All the colour on the page comes from the coin.',
    titleActive:
      'linear-gradient(to bottom,#b9b9c9 0%,#dcdce8 4%,#d2d2e0 6%,#cacada 8%,#c3c3d5 10%,#bdbdd0 14%,#b4b4c8 20%,#b2b2c6 24%,#b6b6cb 56%,#bdbdd2 66%,#c6c6da 76%,#b8b8cd 86%,#a2a2ba 92%,#8c8ca4 94%,#76768c 100%)',
    titleInactive:
      'linear-gradient(to bottom,#dedee6 0%,#e6e6ee 3%,#ededf4 6%,#efeff5 8%,#e7e7ef 14%,#e3e3eb 17%,#dfdfe8 25%,#e1e1ea 56%,#e9e9f1 81%,#e5e5ee 94%,#eaeaf0 100%)',
    grip: '#8c8ca4',
    btnGlow: '#d0d0e0',
    btnFace:
      'radial-gradient(circle at 90% 90%,#8c8ca4 0%,#a2a2ba 55%,#bdbdd2 70%,#dedee8 90%,white 100%)',
    titleText: '#1a1a26',
    taskbar: 'linear-gradient(to bottom,#c6c6d4 0%,#e0e0ea 3%,#d6d6e2 6%,#cfcfdc 10%,#bfbfce 88%,#a8a8b8 96%,#9494a4 100%)',
    start: 'linear-gradient(to bottom,#d7ff35,#ccff00 40%,#a8d400 90%,#89ab00)',
    startText: '#1d2a00',
    accent: '#ccff00',
  },
};

export const NAMES = Object.keys(PRESETS);

function stored() {
  try {
    return window.localStorage.getItem(STORE_KEY);
  } catch (e) {
    return null;
  }
}

/** Resolve the active preset: URL wins, then a stored choice, then the default. */
function resolve() {
  if (typeof window === 'undefined') return DEFAULT;
  try {
    const q = new URLSearchParams(window.location.search).get('chrome');
    if (q === 'off') {
      window.localStorage.removeItem(STORE_KEY);
      return DEFAULT;
    }
    if (q && PRESETS[q]) {
      window.localStorage.setItem(STORE_KEY, q);
      return q;
    }
  } catch (e) {
    /* no URL or no storage: fall through */
  }
  const s = stored();
  return s && PRESETS[s] ? s : DEFAULT;
}

let active = resolve();

export function chromeName() {
  return active;
}

/** The active preset's values. Components read from this. */
export function chrome() {
  return PRESETS[active] || PRESETS[DEFAULT];
}

/** Switch live. Returns the new name. Components re-render via the subscription below. */
export function setChrome(name) {
  if (!PRESETS[name]) return active;
  active = name;
  try {
    window.localStorage.setItem(STORE_KEY, name);
  } catch (e) {
    /* private window */
  }
  subs.forEach(fn => {
    try {
      fn(active);
    } catch (e) {
      /* a bad subscriber must not break the theme */
    }
  });
  return active;
}

/** Step to the next preset — the reroll. */
export function rollChrome() {
  const i = NAMES.indexOf(active);
  return setChrome(NAMES[(i + 1) % NAMES.length]);
}

/** React hook: returns the active preset and re-renders on change. */
export function useChrome() {
  const [, bump] = React.useState(0);
  React.useEffect(() => subscribeChrome(() => bump(n => n + 1)), []);
  return chrome();
}

const subs = new Set();
export function subscribeChrome(fn) {
  subs.add(fn);
  return () => subs.delete(fn);
}

export default chrome;
